// T-026 Phase 4 — wire items table to Realtime with React Query invalidation.
//
// Mirrors useQcRealtime.js (Phase 3, commit 4430507) pattern but thinner:
// - One channel (items table only) vs QC's three.
// - Tier A broad only:
//     ['items']         — prefix-matches both useItemsQuery and
//                         useAllItemsQuery (currently dormant — no
//                         consumers in src/ — kept for forward-compat
//                         per Phase 7 roadmap migration of Catalog grid
//                         from useState to React Query).
//     ['portal-counts'] — Welcome screen item count + brands
//                         (usePortalCounts.js:50).
// - No Tier A narrow: no ['item', id] consumer exists in src/.
// - No Tier B: no catalog-analytics keys exist; sales analytics queries
//   sales_lines / sales_data_master / sales_batches, not items, so no
//   items-driven herd to debounce.
//
// Sub-second cross-tab grid sync is handled by a sibling legacy channel
// bridge in useAppData.js ('items-changes' channel) that drives the
// setItems useState backbone. This hook handles RQ invalidation only.
// Duplicate subscriptions are cheap: Supabase multiplexes the WebSocket
// and React Query invalidation is idempotent.

import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useRealtimeSubscription } from './useRealtimeSubscription';

const TIER_A_BROAD_KEYS = [
  ['items'],
  ['portal-counts'],
];

export function useCatalogRealtime({ enabled = true } = {}) {
  const queryClient = useQueryClient();

  useRealtimeSubscription({
    table: 'items',
    event: '*',
    enabled,
    onPayload: (events) => {
      const affectedIds = events
        .map(e => e.new?.id || e.old?.id)
        .filter(Boolean);

      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: 'catalog.items.tier_a_invalidated',
        data: {
          batch_size: events.length,
          affected_ids: affectedIds.slice(0, 5),
        },
      });

      try {
        TIER_A_BROAD_KEYS.forEach(key => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { source: 'useCatalogRealtime.items.tierABroad' },
        });
      }
    },
  });
}
